import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Box,
  TextField,
  Typography,
  Container,
  IconButton,
} from "@mui/material";
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useStyles } from "./styles";
import { useSelector, useDispatch } from "react-redux";
import { SetColor } from "../../store/Color/ColorReducer";
import { useFormik } from "formik";
import useAuthStore from "../../hooks/store/auth";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../api/dashboardResponse";
import {
  getWidgetScriptSettings,
  updateWidgetScriptSettings,
} from "../../api/business";

const sideNavColor = [
  // "#222059",
  "#5D59D9",
  "#9195D9",
  "#ADB0D9",
  "#A3D9D9",
  "#8F93D9",
  "#D9A362",
  // "#0D0D0D",
  "#ADEDC2",
  "#C4F4F9",
  "#63BFF4",
  "#E949DB",
];

const Index = () => {
  const { updateAction, email } = useAuthStore();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      id: "",
      password: "",
      confirm: "",
      submit: null,
    },
    validationSchema: Yup.object({
      password: Yup.string().max(255).required("Password is required"),
      confirm: Yup.string()
        .max(255)
        .required("Password is required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        if (!email) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: "Unable to resolve the logged-in user email" });
          helpers.setSubmitting(false);
          return;
        }
        const result = await updateAction({
          email,
          password: values.password,
          confirm: values.confirm,
          redirectTo: "/",
          logoutAfterSuccess: true,
        });

        if (result?.type === "auth/update/fulfilled") {
          navigate("/");
          return;
        }

        helpers.setStatus({ success: false });
        helpers.setErrors({
          submit: result?.payload || "Unable to update password",
        });
        helpers.setSubmitting(false);
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  const classes = useStyles();

  const [themeColor, setThemeColor] = useState(null);
  const colorSet = useSelector((state) => state.color);
  const dispatch = useDispatch();
  const setColorChange = () => {};
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [widgetBundleSettings, setWidgetBundleSettings] = useState({
    stagingBundleUrl: "",
    prodBundleUrl: "",
    legacyBundleUrl: "",
  });
  const [widgetScriptLoading, setWidgetScriptLoading] = useState(false);
  const [widgetScriptSaving, setWidgetScriptSaving] = useState(false);

  useEffect(() => {
    setWidgetScriptLoading(true);
    getWidgetScriptSettings()
      .then((res) => {
        const body = unwrapDashboardResponse(
          res,
          "Failed to load widget script settings"
        );
        setWidgetBundleSettings({
          stagingBundleUrl: body?.stagingBundleUrl || "",
          prodBundleUrl: body?.prodBundleUrl || "",
          legacyBundleUrl: body?.legacyBundleUrl || "",
        });
      })
      .catch((error) => {
        toast.error(
          getDashboardErrorMessage(error, "Failed to load widget script settings")
        );
      })
      .finally(() => {
        setWidgetScriptLoading(false);
      });
  }, []);

  const handleWidgetSettingsChange = (event) => {
    const { name, value } = event.target;
    setWidgetBundleSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleWidgetSettingsSave = () => {
    setWidgetScriptSaving(true);
    updateWidgetScriptSettings({
      widgetStagingBundleUrl: widgetBundleSettings.stagingBundleUrl,
      widgetProdBundleUrl: widgetBundleSettings.prodBundleUrl,
      widgetLegacyBundleUrl: widgetBundleSettings.legacyBundleUrl,
    })
      .then((res) => {
        unwrapDashboardResponse(res, "Failed to save widget script settings");
        toast.success("Widget script settings saved");
      })
      .catch((error) => {
        toast.error(
          getDashboardErrorMessage(error, "Failed to save widget script settings")
        );
      })
      .finally(() => {
        setWidgetScriptSaving(false);
      });
  };

  return (
    <>
      <IconButton onClick={setColorChange}></IconButton>
      <Box component="main" sx={{ flewGrow: 1 }}>
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Typography variant="h4" sx={{ px: 0 }}>
              Settings
            </Typography>

            <Card variant="outlined">
              <CardHeader
                title="Theme"
                subheader="Update theme"
                // className={classes.heading}
                variant="h4"
              />
              <Divider />
              <CardContent>
                <Typography variant="h6">Select Theme</Typography>
                <Stack spacing={2} sx={{ maxWidth: 400, p: 3 }}>
                  <Stack sx={{ display: "flex", flexDirection: "row", mr: 4 }}>
                    {sideNavColor.map((color) => (
                      <div>
                        <button
                          style={{
                            width: "15%",
                            padding: 19,
                            margin: 7,
                            borderRadius: "10%",
                            backgroundColor: `${color}`,
                          }}
                          className={classes.themeButton}
                          onClick={() => setThemeColor(color)}
                        ></button>
                      </div>
                    ))}
                  </Stack>
                  <Stack>{/* <IconButton></IconButton> */}</Stack>
                </Stack>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: "flex-end", py: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: themeColor,
                    ":hover": { backgroundColor: themeColor },
                  }}
                  onClick={() => dispatch(SetColor(themeColor))}
                >
                  Change Theme
                </Button>
              </CardActions>
            </Card>

            <form noValidate onSubmit={formik.handleSubmit}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader
                  title="Widget bundle URLs"
                  subheader="Manage legacy, staging and production script sources"
                  variant="h4"
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3} sx={{ maxWidth: 700 }}>
                    <TextField
                      fullWidth
                      label="Legacy bundle URL"
                      name="legacyBundleUrl"
                      value={widgetBundleSettings.legacyBundleUrl}
                      onChange={handleWidgetSettingsChange}
                      disabled={widgetScriptLoading || widgetScriptSaving}
                    />
                    <TextField
                      fullWidth
                      label="Staging bundle URL"
                      name="stagingBundleUrl"
                      value={widgetBundleSettings.stagingBundleUrl}
                      onChange={handleWidgetSettingsChange}
                      disabled={widgetScriptLoading || widgetScriptSaving}
                    />
                    <TextField
                      fullWidth
                      label="Prod bundle URL"
                      name="prodBundleUrl"
                      value={widgetBundleSettings.prodBundleUrl}
                      onChange={handleWidgetSettingsChange}
                      disabled={widgetScriptLoading || widgetScriptSaving}
                    />
                  </Stack>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleWidgetSettingsSave}
                    disabled={widgetScriptLoading || widgetScriptSaving}
                    sx={{
                      backgroundColor: colorSet.color,
                      ":hover": { backgroundColor: colorSet.color },
                      color: "white",
                    }}
                  >
                    {widgetScriptSaving ? "Saving..." : "Save URLs"}
                  </Button>
                </CardActions>
              </Card>

              <Card variant="outlined">
                <CardHeader
                  title="Password"
                  subheader="Change password"
                  variant="h4"
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3} sx={{ maxWidth: 400 }}>
                    <TextField
                      error={
                        !!(formik.touched.password && formik.errors.password)
                      }
                      helperText={
                        formik.touched.password && formik.errors.password
                      }
                      fullWidth
                      label="Password"
                      name="password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      // type="password"
                      value={formik.values.password}
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <Visibility/> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      error={
                        !!(formik.touched.confirm && formik.errors.confirm)
                      }
                      helperText={
                        formik.touched.confirm && formik.errors.confirm
                      }
                      fullWidth
                      label="Password (Confirm)"
                      name="confirm"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      // type="password"
                      value={formik.values.confirm}
                      type={showConfirmPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{
                      backgroundColor: colorSet.color,
                      ":hover": { backgroundColor: colorSet.color },
                      color: 'white',
                    }}
                  >
                    Update
                  </Button>
                </CardActions>
              </Card>
            </form>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Index;
